import { isDefined } from "@clipboard-health/util-ts";
import { Box, ButtonBase, Stack } from "@mui/material";
import { useWorkplaceStarredProfessionals } from "@src/appV2/Professionals/api/starredProfessionals/useWorkplaceStarredProfessionals";
import { useWorker } from "@src/appV2/Worker/api/useWorker";

import { useIsMobile } from "../AppLayout/useResponsive";
import { Avatar } from "../components/Avatar";
import { Icon } from "../components/Icon";
import { IconButton } from "../components/IconButton";
import { Text } from "../components/Text";
import { ChatActionsMenu } from "./components/ChatActionsMenu/ChatActionsMenu";
import { useViewProfileHandler } from "./hooks/useViewProfileHandler";
import { useTu } from "@src/appV2/i18n/useTu";

export interface ChatHeaderProps {
  workerId: string;
  /** Resolved workplace ID from `useChatPanelState`, used for star/lock mutations. */
  workplaceId: string;
  workerName: string;
  /** When true, the chat is viewed as the worker (e.g. admin impersonation), so the other party is a facility. */
  isWorkerSession: boolean;
  onClose: () => void;
  onViewProfile?: (workerId: string) => void;
  showViewProfile?: boolean;
  onMarkConversationAsUnread?: () => void;
}

/**
 * Header component for the chat panel.
 * Displays worker avatar, name, qualification, and action buttons.
 */
export function ChatHeader(props: ChatHeaderProps) {
  const { tu } = useTu("chat");
  const {
    workerId,
    workplaceId,
    workerName,
    isWorkerSession,
    onClose,
    onViewProfile,
    showViewProfile,
    onMarkConversationAsUnread,
  } = props;

  // Fetch worker details for avatar and qualification
  const { data: worker } = useWorker(workerId);

  // Surface a star next to the name for starred professionals, mirroring the
  // standalone chat page and the worker profile drawer. The query is shared
  // (and deduped) with ChatActionsMenu's lookup.
  const { isStarredProfessionalEnabled, starredProfessionalId } = useWorkplaceStarredProfessionals({
    workplaceId,
    workerId,
  });
  const isStarred =
    !isWorkerSession && isStarredProfessionalEnabled && isDefined(starredProfessionalId);

  const isMobile = useIsMobile();
  const buttonSize = isMobile ? "medium" : "small";
  const handleViewProfile = useViewProfileHandler({ workerId, onViewProfile });
  const profileLinkEnabled = showViewProfile !== false;

  const avatar = (
    <Avatar size="medium" src={worker?.profileImageUrl ?? undefined} alt={workerName} />
  );
  const nameText = (
    <Text
      inline
      semibold
      variant="h6"
      className="worker-name"
      sx={(theme) => ({
        color: theme.palette.base?.base,
        fontSize: theme.typography?.body1?.fontSize,
      })}
    >
      {workerName}
    </Text>
  );

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 3,
      }}
    >
      {/* Left: Worker info */}
      <Stack direction="row" alignItems="center" gap={3}>
        {profileLinkEnabled ? (
          <ButtonBase
            aria-label={`View ${workerName}'s profile`}
            sx={(theme) => ({
              borderRadius: "50%",
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.base?.primary}`,
                outlineOffset: 2,
              },
            })}
            onClick={handleViewProfile}
          >
            {avatar}
          </ButtonBase>
        ) : (
          avatar
        )}
        <Stack direction="column" gap={1} sx={{ minWidth: 0 }}>
          {profileLinkEnabled ? (
            <ButtonBase
              sx={(theme) => ({
                alignSelf: "flex-start",
                justifyContent: "flex-start",
                textAlign: "left",
                borderRadius: theme.borderRadius?.small ?? theme.spacing(1),
                "&:hover .worker-name": { textDecoration: "underline" },
                "&:focus-visible": {
                  outline: `2px solid ${theme.palette.base?.primary}`,
                  outlineOffset: 2,
                },
              })}
              onClick={handleViewProfile}
            >
              <Stack direction="row" alignItems="center" gap={2}>
                {nameText}
                {isStarred ? (
                  <Icon type="star-five-sides" size="small" sx={{ flexShrink: 0 }} />
                ) : null}
                <Icon
                  type="chevron-right"
                  size="small"
                  sx={(theme) => ({ color: theme.palette.base?.muted, flexShrink: 0 })}
                />
              </Stack>
            </ButtonBase>
          ) : (
            nameText
          )}
          {worker?.qualification && (
            <Text
              variant="caption"
              sx={(theme) => ({
                color: theme.palette.text.secondary,
              })}
            >
              {worker.qualification}
            </Text>
          )}
        </Stack>
      </Stack>

      {/* Right: Actions */}
      <Stack direction="row" alignItems="center" gap={2}>
        <ChatActionsMenu
          worker={worker}
          workerId={workerId}
          workerName={workerName}
          workplaceId={workplaceId}
          size={buttonSize}
          onMarkConversationAsUnread={onMarkConversationAsUnread}
        />
        <IconButton
          circular
          aria-label={tu("Close chat")}
          iconType="close"
          size={buttonSize}
          variant="outlined"
          onClick={onClose}
        />
      </Stack>
    </Box>
  );
}
